/* ============================================================
   SAHAB SYSTEMS — brand primitives (TS port of design/brand.jsx)
   MCP-core mark, lockups, icon set. All themed via CSS vars.
   ============================================================ */
import type { CSSProperties, SVGProps } from "react";

type MarkProps = { size?: number; pulse?: boolean; className?: string };

/* The mark: telemetry ring with eight radiating nodes (alternating
   circle/square caps) + a center core. The TOP spoke is the cyan pulse.
   Ink parts use currentColor so the mark themes with text. */
export function Mark({ size = 120, pulse = false, className = "" }: MarkProps) {
  const ink = "currentColor";
  const sig: CSSProperties = { fill: "var(--signal)" };
  const sigS: CSSProperties = { stroke: "var(--signal)" };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      className={(pulse ? "is-pulsing " : "") + className}
      role="img"
      aria-label="Sahab Systems"
      style={{ display: "block" }}
    >
      <circle cx="60" cy="60" r="26" fill="none" stroke={ink} strokeWidth="1.6" opacity=".9" />
      {/* top pulse spoke */}
      <line x1="60" y1="46" x2="60" y2="22" strokeWidth="2.2" strokeLinecap="round" style={sigS} />
      <rect x="56.6" y="18.6" width="6.8" height="6.8" rx=".8" className="pulse-node" style={sig} />
      {/* 7 other spokes (ink) */}
      <line x1="69.9" y1="50.1" x2="86.87" y2="33.13" stroke={ink} strokeWidth="1.6" strokeLinecap="round" opacity=".9" />
      <circle cx="86.87" cy="33.13" r="3.2" fill={ink} />
      <line x1="74" y1="60" x2="98" y2="60" stroke={ink} strokeWidth="1.6" strokeLinecap="round" opacity=".9" />
      <rect x="94.8" y="56.8" width="6.4" height="6.4" rx=".8" fill={ink} />
      <line x1="69.9" y1="69.9" x2="86.87" y2="86.87" stroke={ink} strokeWidth="1.6" strokeLinecap="round" opacity=".9" />
      <circle cx="86.87" cy="86.87" r="3.2" fill={ink} />
      <line x1="60" y1="74" x2="60" y2="98" stroke={ink} strokeWidth="1.6" strokeLinecap="round" opacity=".9" />
      <rect x="56.8" y="94.8" width="6.4" height="6.4" rx=".8" fill={ink} />
      <line x1="50.1" y1="69.9" x2="33.13" y2="86.87" stroke={ink} strokeWidth="1.6" strokeLinecap="round" opacity=".9" />
      <circle cx="33.13" cy="86.87" r="3.2" fill={ink} />
      <line x1="46" y1="60" x2="22" y2="60" stroke={ink} strokeWidth="1.6" strokeLinecap="round" opacity=".9" />
      <rect x="18.8" y="56.8" width="6.4" height="6.4" rx=".8" fill={ink} />
      <line x1="50.1" y1="50.1" x2="33.13" y2="33.13" stroke={ink} strokeWidth="1.6" strokeLinecap="round" opacity=".9" />
      <circle cx="33.13" cy="33.13" r="3.2" fill={ink} />
      {/* core */}
      <circle cx="60" cy="60" r="11" fill={ink} />
      <circle cx="60" cy="60" r="6.5" fill="none" strokeWidth="1.8" style={sigS} />
      <circle cx="60" cy="60" r="2.4" style={sig} />
    </svg>
  );
}

/* Horizontal lockup: mark · hairline rule · wordmark.
   Built in HTML so it themes + uses live webfonts. */
export function Lockup({
  height = 34,
  mark = true,
  pulse = false,
}: {
  height?: number;
  mark?: boolean;
  pulse?: boolean;
}) {
  const fs = height * 0.8;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: height * 0.34, color: "var(--text)" }}>
      {mark && <Mark size={height * 1.18} pulse={pulse} />}
      {mark && <div style={{ width: 1, height: height * 0.86, background: "var(--line)" }} />}
      <div
        style={{
          fontWeight: 800,
          fontSize: fs,
          letterSpacing: "-0.035em",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        sahab<span style={{ color: "var(--signal)" }}>.</span>
        <span style={{ color: "var(--text-2)", fontWeight: 700 }}>systems</span>
      </div>
    </div>
  );
}

/* Bilingual footer lockup. Uses var(--font-ar) for the Arabic trade name
   and var(--sand) per the brand. Always rendered on the public card,
   regardless of UI language. */
export function BilingualLockup({ height = 30 }: { height?: number }) {
  const fs = height * 0.92;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: height * 0.4,
        color: "var(--text)",
        flexWrap: "wrap",
        justifyContent: "center",
      }}
    >
      <Mark size={height * 1.18} />
      <div style={{ width: 1, height: height * 0.86, background: "var(--line)" }} />
      <div
        style={{
          fontWeight: 800,
          fontSize: fs,
          letterSpacing: "-0.035em",
          lineHeight: 1,
          whiteSpace: "nowrap",
        }}
      >
        sahab<span style={{ color: "var(--signal)" }}>.</span>
        <span style={{ color: "var(--text-2)", fontWeight: 700 }}>systems</span>
      </div>
      <div style={{ width: 1, height: height * 0.86, background: "var(--line)" }} />
      <div
        style={{
          fontFamily: "var(--font-ar)",
          fontWeight: 700,
          fontSize: fs * 0.86,
          color: "var(--sand)",
          lineHeight: 1,
        }}
      >
        منظومة السحاب
      </div>
    </div>
  );
}

/* ---- icon set: 1.6 stroke, currentColor, 20px grid ---- */
type IcProps = {
  d?: string;
  fill?: boolean;
  size?: number;
  sw?: number;
  vb?: number;
  children?: React.ReactNode;
  style?: CSSProperties;
} & Omit<SVGProps<SVGSVGElement>, "fill" | "stroke">;

function Ic({ d, fill, size = 20, sw = 1.6, vb = 24, children, style, ...rest }: IcProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${vb} ${vb}`}
      fill={fill ? "currentColor" : "none"}
      stroke={fill ? "none" : "currentColor"}
      strokeWidth={sw}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flex: "none", ...style }}
      aria-hidden="true"
      {...rest}
    >
      {children || (d && <path d={d} />)}
    </svg>
  );
}

type IconFn = (p?: { size?: number; style?: CSSProperties }) => JSX.Element;

type IconSet = {
  phone: IconFn;
  mail: IconFn;
  pin: IconFn;
  globe: IconFn;
  linkedin: IconFn;
  whatsapp: IconFn;
  qr: IconFn;
  download: IconFn;
  share: IconFn;
  search: IconFn;
  filter: IconFn;
  plus: IconFn;
  x: IconFn;
  chevR: IconFn;
  chevD: IconFn;
  edit: IconFn;
  check: IconFn;
  arrowR: IconFn;
  users: IconFn;
  list: IconFn;
  logout: IconFn;
  calendar: IconFn;
  building: IconFn;
  sun: IconFn;
  moon: IconFn;
  ext: IconFn;
  copy: IconFn;
  dots: IconFn;
  note: IconFn;
  alert: IconFn;
  inbox: IconFn;
  refresh: IconFn;
};

export const I: IconSet = {
  phone: (p) => (
    <Ic
      {...p}
      d="M3 5.5C3 4.1 4.1 3 5.5 3H7l1.5 4-2 1.5a13 13 0 0 0 6 6l1.5-2 4 1.5v1.5c0 1.4-1.1 2.5-2.5 2.5A15.5 15.5 0 0 1 3 5.5Z"
    />
  ),
  mail: (p) => (
    <Ic {...p}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3.5 7 8.5 6 8.5-6" />
    </Ic>
  ),
  pin: (p) => (
    <Ic {...p}>
      <path d="M20 10c0 5.5-8 12-8 12s-8-6.5-8-12a8 8 0 0 1 16 0Z" />
      <circle cx="12" cy="10" r="2.8" />
    </Ic>
  ),
  globe: (p) => (
    <Ic {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18M12 3c2.6 2.5 2.6 15.5 0 18M12 3c-2.6 2.5-2.6 15.5 0 18" />
    </Ic>
  ),
  linkedin: (p) => (
    <Ic
      {...p}
      fill
      d="M4.98 3.5A2.5 2.5 0 1 0 5 8.5a2.5 2.5 0 0 0 0-5ZM3 9.5h4v11H3v-11Zm6.5 0h3.8v1.5h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1v5.45h-4v-4.83c0-1.15-.02-2.63-1.6-2.63-1.6 0-1.85 1.25-1.85 2.55v4.91h-4v-11Z"
    />
  ),
  whatsapp: (p) => (
    <Ic
      {...p}
      fill
      vb={24}
      d="M12.04 2C6.6 2 2.2 6.4 2.2 11.84c0 1.94.55 3.76 1.5 5.3L2 22l4.98-1.62a9.8 9.8 0 0 0 5.06 1.4h.01c5.43 0 9.84-4.4 9.84-9.84C21.88 6.4 17.47 2 12.04 2Zm5.74 13.86c-.24.68-1.42 1.32-1.95 1.36-.53.05-1.02.24-3.44-.72-2.9-1.14-4.73-4.1-4.87-4.3-.14-.18-1.16-1.54-1.16-2.94 0-1.4.73-2.08 1-2.37.24-.27.53-.34.71-.34l.5.01c.16.01.38-.06.6.46.23.55.78 1.9.85 2.04.07.14.12.3.02.48-.1.18-.14.3-.28.46-.14.16-.3.36-.42.48-.14.14-.29.29-.12.57.16.27.74 1.22 1.6 1.98 1.1.98 2.03 1.28 2.3 1.42.28.14.44.12.6-.07.18-.2.7-.81.88-1.09.18-.27.36-.23.6-.14.25.09 1.6.76 1.87.9.27.14.46.2.53.32.07.12.07.69-.17 1.36Z"
    />
  ),
  qr: (p) => (
    <Ic {...p}>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3M21 14v.01M17 21h4v-4M14 21v.01" />
    </Ic>
  ),
  download: (p) => <Ic {...p} d="M12 3v12m0 0 4-4m-4 4-4-4M4 19h16" />,
  share: (p) => (
    <Ic {...p}>
      <path d="M12 15V4m0 0 3.5 3.5M12 4 8.5 7.5" />
      <path d="M5 12v6.5C5 19.3 5.7 20 6.5 20h11c.8 0 1.5-.7 1.5-1.5V12" />
    </Ic>
  ),
  search: (p) => (
    <Ic {...p}>
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.2-3.2" />
    </Ic>
  ),
  filter: (p) => <Ic {...p} d="M3 5h18l-7 8v6l-4-2v-4L3 5Z" />,
  plus: (p) => <Ic {...p} d="M12 5v14M5 12h14" />,
  x: (p) => <Ic {...p} d="M6 6l12 12M18 6 6 18" />,
  chevR: (p) => <Ic {...p} d="m9 6 6 6-6 6" />,
  chevD: (p) => <Ic {...p} d="m6 9 6 6 6-6" />,
  edit: (p) => (
    <Ic {...p}>
      <path d="M4 20h4L18.5 9.5a2 2 0 0 0-3-3L5 17v3Z" />
      <path d="M13.5 7.5l3 3" />
    </Ic>
  ),
  check: (p) => <Ic {...p} d="m4 12 5 5L20 6" />,
  arrowR: (p) => <Ic {...p} d="M4 12h16m0 0-6-6m6 6-6 6" />,
  users: (p) => (
    <Ic {...p}>
      <circle cx="9" cy="8" r="3.2" />
      <path d="M3 20c0-3.3 2.7-5 6-5s6 1.7 6 5" />
      <path d="M16 5a3 3 0 0 1 0 6M21 20c0-2.6-1.5-4.2-3.5-4.8" />
    </Ic>
  ),
  list: (p) => (
    <Ic {...p} d="M8 6h13M8 12h13M8 18h13M3.5 6h.01M3.5 12h.01M3.5 18h.01" />
  ),
  logout: (p) => (
    <Ic {...p}>
      <path d="M15 5V4a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-1" />
      <path d="M19 12H9m10 0-3-3m3 3-3 3" />
    </Ic>
  ),
  calendar: (p) => (
    <Ic {...p}>
      <rect x="3.5" y="5" width="17" height="16" rx="2" />
      <path d="M3.5 9.5h17M8 3v4M16 3v4" />
    </Ic>
  ),
  building: (p) => (
    <Ic {...p}>
      <rect x="5" y="3" width="14" height="18" rx="1.5" />
      <path d="M9 7h2M13 7h2M9 11h2M13 11h2M9 15h2M13 15h2M10 21v-3h4v3" />
    </Ic>
  ),
  sun: (p) => (
    <Ic {...p}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
    </Ic>
  ),
  moon: (p) => <Ic {...p} d="M20 13.5A8 8 0 0 1 10.5 4 7 7 0 1 0 20 13.5Z" />,
  ext: (p) => (
    <Ic {...p} d="M14 4h6v6M20 4l-8 8M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6" />
  ),
  copy: (p) => (
    <Ic {...p}>
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a1 1 0 0 1 1-1h10" />
    </Ic>
  ),
  dots: (p) => (
    <Ic {...p} fill>
      <circle cx="5" cy="12" r="1.8" />
      <circle cx="12" cy="12" r="1.8" />
      <circle cx="19" cy="12" r="1.8" />
    </Ic>
  ),
  note: (p) => (
    <Ic {...p}>
      <path d="M5 3h10l4 4v14a0 0 0 0 1 0 0H5a0 0 0 0 1 0 0V3Z" />
      <path d="M14 3v4h4M8 12h8M8 16h5" />
    </Ic>
  ),
  alert: (p) => (
    <Ic {...p}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 8v5M12 16h.01" />
    </Ic>
  ),
  inbox: (p) => (
    <Ic {...p}>
      <path d="M4 13h4l1.5 3h5L16 13h4" />
      <path d="M5 13 7 5h10l2 8v5a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1v-5Z" />
    </Ic>
  ),
  refresh: (p) => (
    <Ic {...p}>
      <path d="M4 12a8 8 0 0 1 13.7-5.6L20 8M20 4v4h-4" />
      <path d="M20 12a8 8 0 0 1-13.7 5.6L4 16M4 20v-4h4" />
    </Ic>
  ),
};

/* Initials fallback for Avatar (matches design/card.jsx). */
export function initials(name: string): string {
  return name
    .replace(/,.*$/, "")
    .replace(/\b(PhD|Dr|د\.?)\b/gi, "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}
